import IcrasForm from '@scripts/forms/IcrasForm';
import IcrasHelpers from '@scripts/helpers';

export default class {
  /**
   * Main form
   *
   * @protected
   *
   * @type {IcrasForm}
   */
  _form;

  /**
   * Form messages indexed by type
   *
   * @protected
   *
   * @type {object}
   * @property {Array<string>} errors
   * @property {Array<string>} warnings
   * @property {Array<string>} success
   */
  _messages = {
    errors: [],
    warnings: [],
    success: [],
  };

  /**
   * @param {IcrasForm} form Global form
   */
  constructor(form) {
    this._form = form;
  }

  /**
   * @protected
   *
   * @param {string} type Type of message: 'errors', 'warnings', 'success'
   * @param {Array<string>} messages Messages to add
   *
   * @return {void}
   */
  _addMessages(type, messages) {
    this._messages[type] = [...(this._messages[type] || []), ...messages];
  }

  /**
   * Get messages by type
   *
   * @protected
   *
   * @param {string} type Type of message
   *
   * @return {Array<string>}
   */
  __getMessages(type) {
    return this._messages[type] || [];
  }

  /**
   * Reset messages
   *
   * @protected
   *
   * @return {void}
   */
  _resetMessages() {
    this._messages = {
      errors: [],
      warnings: [],
      success: [],
    };
  }

  /**
   * @protected
   *
   * @param {string} content Popin content
   *
   * @return {void}
   */
  _openPopin(content = '') {
    this._form.openPopin(
      IcrasHelpers.buildPopinContent(
        content,
        this.errors,
        this.warnings,
        this.success
      )
    );
  }

  /**
   * Initialize the form part (e.g.: event listeners)
   *
   * Override this method to perform actions after the main form has been initialized
   *
   * @return {void}
   */
  initialize() {
    // Empty method
  }

  /**
   * @return {boolean}
   */
  validate() {
    return this._messages['errors'].length === 0;
  }

  /**
   * Reset the fields of the form part
   *
   * @returns {void}
   */
  reset() {
    this._resetMessages();
  }

  /**
   * If patient has ASCVD risk factors
   *
   * @return {bool}
   */
  hasAscvdRiskFactors() {
    return false;
  }

  /**
   * @param {Array} errors Error messages
   *
   * @return {void}
   */
  addErrors(errors) {
    this._addMessages('errors', errors);
  }

  /**
   * @param {Array} warnings Warning messages
   *
   * @return {void}
   */
  addWarnings(warnings) {
    this._addMessages('warnings', warnings);
  }

  /**
   * @param {Array} success Success messages
   *
   * @return {void}
   */
  addSuccess(success) {
    this._addMessages('success', success);
  }

  /**
   * @return {Array}
   */
  get element() {
    return this._form;
  }

  /**
   * @return {Array<string>}
   */
  get errors() {
    return this.__getMessages('errors');
  }

  /**
   * @return {Array<string>}
   */
  get warnings() {
    return this.__getMessages('warnings');
  }

  /**
   * @return {Array<string>}
   */
  get success() {
    return this.__getMessages('success');
  }
}
